import { React, PT, WithData, fetchApi } from '@gasbuddy/react';
import { first } from 'lodash';
import moment from 'moment';
import BN from 'bignumber.js';
import {
  Loader, Dimmer, Segment, Table, Divider, Grid, List,
  Header, Button, Modal, Icon, Container, Popup, Message,
} from 'semantic-ui-react';
import JSONTree from 'react-json-tree';
import { fetch } from '../../../client/lib/fetch';
import { Auth, Nav, ShipModal, ReissueModal, NoteTable } from '../../components';
import { Role } from '../../roles';
import { InstrumentTools } from '../../devtools/InstrumentTools';
import { ChangeNameModal } from '../../components/payments/changeNameModal';
import { LimitConfirmationModal } from '../../components/payments/LimitConfirmationModal';
import { SpendLimits } from '../../components/payments/SpendLimits';

function pre(obj) {
  if (!obj) {
    return '';
  }
  return (
    <pre>{JSON.stringify(obj, null, '\t')}</pre>
  );
}

function bvColor(v) {
  if (v.debit_status === 'settled') {
    return 'green';
  }
  if (v.debit_status === 'declined') {
    return 'red';
  }
  return 'grey';
}

function bvIcon(v) {
  if (v.debit_status === 'settled') {
    return 'check circle';
  }
  if (v.debit_status === 'declined') {
    return 'remove circle';
  }
  return 'spinner';
}

function isCardExpiringSoon(d) {
  return d.expiration && moment().isAfter(moment(d.expiration).subtract(1, 'month'));
}

function isGbCard(d) {
  return !!(d && d.instrument && d.instrument.provider_name === 'gasbuddy'
    && d.instrument.instrument_type === 'private_card');
}

function isCorPayCard(d) {
  return !!(d && d.instrument && d.instrument.provider_name === 'pdi'
    && d.instrument.instrument_type === 'private_card');
}

function hasTag(instr, tag) {
  return !!(instr && instr.extra_data && instr.extra_data.tags
    && instr.extra_data.tags.includes(tag));
}

function hasLastDigits(instr) {
  return !!(instr && instr.extra_data && instr.extra_data.last_digits);
}

function isBank(d) {
  return !!(d && d.instrument && d.instrument.instrument_type === 'bank_account');
}

function isDeleted(d) {
  return !!(d && d.instrument && d.instrument.deleted);
}

function isAccountDeleteRequested(d) {
  return !!(d && d.deleteRequested);
}

function isBlocked(d) {
  if (d && d.blocked) {
    return true;
  }
  if (d && d.lookalikes && d.lookalikes.find(l => l.blocked_at)) {
    return true;
  }
  return false;
}

function hasAutopay(d) {
  return d?.paymentOption === 'autopay';
}

function canRerunVerification(data) {
  if (!data) {
    return false;
  }
  if (data.verification.length === 0
    || !data.verification.find(v => v.debit_status !== 'declined')) {
    return true;
  }
  return false;
}

const canDeactivate = instr => (hasTag(instr, 'active') || hasTag(instr, 'user-inactive'))
  && !hasTag(instr, 'inactive');
const canActivate = instr => hasTag(instr, 'inactive');
const hasFulfillment = data => !!first(data.fulfillments);
const hasActiveMembership = data => !!data?.has_active_membership;

class InstrumentDetail extends React.Component {
  state = {
    loading: false,
  }

  componentWillReceiveProps(nextProps) {
    const { data } = this.props;
    const { dwolla } = this.state;
    if (!data.instrument && nextProps.data.instrument) {
      // Fire off post-load requests
      if (isBank(nextProps.data) && !dwolla) {
        this.getDwollaStatus(nextProps.data.instrument);
      }
    }
  }

  setAuthProfile = (name) => {
    const { data, fetch: fetchFn } = this.props;
    this.setState({ loading: true }, async () => {
      await fetch('/payments/instrument/set-auth-profile', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
        name,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  setModal(name) {
    this.setState({ modal: name });
  }

  async getDwollaStatus(instrument) {
    const { body } = await fetch('/payments/instrument/get-dwolla', {
      account_id: instrument.account_id,
      instrument_id: instrument.instrument_id,
    });
    this.setState({ dwolla: body });
  }

  setUserInactiveReleaseInactiveTag() {
    this.setAuthProfile('user-inactive');
    this.removeTag('inactive');
  }

  releaseShipping = (e, id) => {
    e.preventDefault();
    const { fetch: fetchFn } = this.props;
    this.setState({ loading: true }, async () => {
      try {
        await fetch('/payments/instrument-map/fulfillment/release', {
          payment_instrument_fulfillment_id: id,
        });
        this.setState({ loading: false }, () => {
          fetchFn();
        });
      } catch (error) {
        this.setState({ loading: false, error });
      }
    });
  }

  deleteInstrument = () => {
    this.setState({ loading: true, modal: null }, async () => {
      const { data, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/delete', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  undeleteInstrument = () => {
    this.setState({ loading: true, modal: null }, async () => {
      const { data, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/undelete', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  removeTag = (tag) => {
    this.setState({ loading: true }, async () => {
      const { data, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/tags', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
        remove_tags: [tag],
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  removeAndRecordTags = (tag) => {
    this.setState({ loading: true }, async () => {
      const { data, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/remove-record-tags', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
        remove_tags: [tag],
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  refreshToken = () => {
    this.setState({ loading: true, modal: null }, async () => {
      const { data, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/refresh-token', {
        account_id: data.instrument.account_id,
        instrument_id: data.instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  toggleTag = (tag) => {
    this.setState({ loading: true }, async () => {
      const addTags = [];
      const removeTags = [];
      const { data: { instrument }, fetch: fetchFn } = this.props;

      if (hasTag(instrument, tag)) {
        removeTags.push(tag);
      } else {
        addTags.push(tag);
      }

      await fetch('/payments/instrument/tags', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
        add_tags: addTags,
        remove_tags: removeTags,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  reprovision = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/reprovision', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  setAsDefault = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      // eslint-disable-next-line prefer-const
      let { status, body, error } = await fetchApi({
        url: '/payments/instrument/default',
        body: {
          account_id: instrument.account_id,
          instrument_id: instrument.instrument_id,
        },
        catchErrors: true,
      });

      if (status !== 200) {
        error = body;
      }

      this.setState({ loading: false, error }, () => {
        fetchFn();
      });
    });
  }

  renewKickoff = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/renew-offer', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  rerunVerification = () => {
    this.setState({ loading: true, modal: null }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      // eslint-disable-next-line prefer-const
      let { status, body, error } = await fetchApi({
        url: '/payments/instrument/rerun-verification',
        body: {
          account_id: instrument.account_id,
          instrument_id: instrument.instrument_id,
        },
        catchErrors: true,
      });

      if (status !== 200) {
        error = body;
      }

      this.setState({ loading: false, error }, () => {
        fetchFn();
      });
    });
  }

  verifyDwollaStatus = () => {
    this.setState({ loading: true, modal: null }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/verify-dwolla', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  reactivateWex = (e) => {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/wex/reactivate', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  markAsReturned = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument, fulfillments }, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/mark-returned', {
        instrument,
        fulfillment: first(fulfillments),
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  };

  activate = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/corpay/activate', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  deactivate = () => {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/corpay/deactivate', {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  reactivate = (e) => {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      try {
        const { data: { instrument }, fetch: fetchFn } = this.props;
        const { dwolla } = this.state;
        await fetch('/payments/dwolla/reactivate', {
          account_id: instrument.account_id,
          instrument_id: instrument.instrument_id,
          // eslint-disable-next-line no-underscore-dangle
          href: dwolla.customer._links.self.href,
        });
        this.setState({ loading: false, dwolla: null }, () => {
          this.getDwollaStatus(instrument);
          fetchFn();
        });
      } catch (error) {
        this.setState({ loading: false, error });
      }
    });
  }

  switchPaymentType = () => {
    this.setState({ loading: true }, async () => {
      const { data, fetch: fetchFn } = this.props;
      const isAutopay = hasAutopay(data);
      const oppositePaymentType = isAutopay ? 'weekly' : 'auto';
      const accountId = data?.instrument?.account_id;
      const { body, status } = await fetch(`/payments/periodic-payment/${accountId}`, {
        payment_type: oppositePaymentType,
      });
      let error;
      if (status !== 200) {
        error = body;
      }
      this.setState({ loading: false, error }, () => {
        fetchFn();
      });
    });
  };

  investigate(bvid) {
    this.setState({ loading: true }, async () => {
      const { body } = await fetch(`/payments/instrument/investigate/${bvid}`, {});
      this.setState({ loading: false, bvDetail: body });
    });
  }

  reverify(bvid) {
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch(`/payments/instrument/rerun-verification/${bvid}`, {
        account_id: instrument.account_id,
        instrument_id: instrument.instrument_id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  cancelShip({ payment_instrument_fulfillment_id: id }) {
    const { fetch: fetchFn } = this.props;
    this.setState({ loading: true }, async () => {
      await fetch('/payments/instrument-map/fulfillment/delete', {
        payment_instrument_fulfillment_id: id,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  blockAccount(e, blocked) {
    e.preventDefault();
    this.setState({ loading: true }, async () => {
      const { data: { instrument }, fetch: fetchFn } = this.props;
      await fetch('/payments/instrument/set-block', {
        instrument_id: instrument.instrument_id,
        account_id: instrument.account_id,
        blocked,
      });
      this.setState({ loading: false }, () => {
        fetchFn();
      });
    });
  }

  spendLimitUpdated() {
    const { fetch: fetchFn } = this.props;
    fetchFn();
  }

  renderReleaseButton(addr) {
    if (addr.deliverability !== 'deliverable' && !addr.batch_identifier
      && !(addr.deliverability_detail && addr.deliverability_detail.user_verified)) {
      return (
        <Auth hideUnauthorized forRoles={[Role.Superuser, Role.PaymentSupport]}>
          <Button onClick={e => this.releaseShipping(e, addr.payment_instrument_fulfillment_id)}>
            Approve
          </Button>
        </Auth>
      );
    }
    return null;
  }

  render() {
    const { data, loading, mapId, fetch: fetchFn, history } = this.props;
    const { dwollaLoading, dwolla, error, bvDetail, loading: stateLoading, modal } = this.state;
    const isAutopay = hasAutopay(data);
    const oppositePaymentType = isAutopay ? 'Manual Pay' : 'Auto Pay';
    return (
      <Segment>
        <Dimmer active={loading || stateLoading}>
          <Loader />
        </Dimmer>

        <Modal open={!!bvDetail} closeIcon onClose={() => this.setState({ bvDetail: null })}>
          <Modal.Content>
            <JSONTree data={bvDetail} shouldExpandNode={() => true} />
          </Modal.Content>
        </Modal>

        <div>
          {data.instrument && (
            <div style={{ marginBottom: 10 }}>
              {data.cursor && (
                <Button.Group floated="right">
                  {data.cursor.previous && <Button icon onClick={() => history.push(`/payments/instrument-map/${data.cursor.previous}`)}><Icon name="left chevron" /></Button>}
                  {data.cursor.next && <Button icon onClick={() => history.push(`/payments/instrument-map/${data.cursor.next}`)}><Icon name="right chevron" /></Button>}
                </Button.Group>
              )}
              <h1>
Payment Instrument #
                <span>{data.instrument.instrument_id}</span>
              </h1>
              {isDeleted(data)
                && (
                <Message warning>
                  <b>DELETED ON</b>
                  {' '}
                  {data.instrument.deleted
                    ? moment(data.instrument.deleted).format('MMM DD, YYYY hh:mm:ss a ZZ') : 'n/a'}
                </Message>
                )
              }
              {isBlocked(data)
                && (
                <Message error>
                  <b>BLOCKED</b>
: The account number used for this instrument cannot be used
                  to create new instruments and cannot be used to activate a GasBuddy Payment Card.
                </Message>
                )
              }
              <div>
                <b>Account:</b>
&nbsp;
                <a href={`/identity/users/${data.instrument.account_id}`}>
                  {data.instrument.account_id}
                </a>
              </div>
              <div>
                <b>Provider:</b>
&nbsp;
                {data.instrument.provider_name}
              </div>
              <div>
                <b>Instrument Type:</b>
&nbsp;
                {data.instrument.instrument_type}
              </div>
              <div>
                <b>Created:</b>
&nbsp;
                {data.instrument.created
                  ? moment(data.instrument.created).format('MMM DD, YYYY hh:mm:ss a ZZ') : 'n/a'}
              </div>
              {data.instrument.extra_data && data.instrument.extra_data.tags && (
                <div>
                  <b>Tags:</b>
&nbsp;
                  {data.instrument.extra_data.tags.join(', ')}
                </div>
              )}
            </div>
          )}
          <Container fluid textAlign="right">
            {error
              && (
              <Modal open>
                <Modal.Header>An Error Has Occurred</Modal.Header>
                <Modal.Content>{error.message}</Modal.Content>
                <Modal.Actions>
                  <Button color="green" inverted onClick={() => this.setState({ error: null })}>
                    <Icon name="checkmark" />
                    {' '}
OK
                  </Button>
                </Modal.Actions>
              </Modal>
              )
            }
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <span>
                {(isGbCard(data) || isCorPayCard(data)) && !hasLastDigits(data.instrument) && !isDeleted(data)
                  && <Button onClick={() => this.reprovision()}>Reprovision Card</Button>
                }
                {isCorPayCard(data) && hasTag(data.instrument, 'active') && !hasActiveMembership(data)
                  && <Button onClick={() => this.switchPaymentType()}>{`Switch to ${oppositePaymentType}`}</Button>}
                {isCorPayCard(data) && hasTag(data.instrument, 'active') && hasAutopay(data) && (
                  <SpendLimits
                    instrument={data.instrument}
                    spendLimits={data.spendLimits}
                    currentSpendLimit={data.currentSpendLimit}
                    onUpdate={() => this.spendLimitUpdated()}
                  />
                )}
                {isCorPayCard(data) && canActivate(data.instrument)
                  && <Button onClick={() => this.activate()}>Assign Active Auth Profile</Button>
                }
                {isCorPayCard(data) && canDeactivate(data.instrument)
                  && <Button onClick={() => this.deactivate()}>Assign Inactive Auth Profile</Button>
                }
                {isGbCard(data) && hasTag(data.instrument, 'active')
                  && <Button onClick={() => this.setAuthProfile('inactive')}>Assign Inactive Auth Profile</Button>
                }
                {isGbCard(data) && !hasTag(data.instrument, 'active') && !isAccountDeleteRequested(data)
                  && <Button onClick={() => this.setAuthProfile('active')}>Assign Active Auth Profile</Button>
                }

                {isGbCard(data) && !hasTag(data.instrument, 'user-inactive')
                  && <Button onClick={() => this.setUserInactiveReleaseInactiveTag()}>Assign User-Inactive Auth Profile</Button>
                }
                {isGbCard(data) && hasTag(data.instrument, 'inactive')
                  && (
                  <span>
                    <Popup
                      trigger={
                        <Button onClick={() => this.removeTag('inactive')}>Remove inactive tag</Button>
                      }
                      content="If the user should be able to activate this card themselves, remove the inactive tag"
                    />
                    <Popup
                      trigger={
                        <Button onClick={() => this.toggleTag('reactivate')}>Toggle Auto-reactivation</Button>
                      }
                      content="Toggles the 'reactivate' tag. Inactive cards with this tag on will be set to active when the backend detects the account is in good standing"
                    />
                  </span>
                  )
                }
                {isGbCard(data) && hasLastDigits(data.instrument)
                  && <Button onClick={() => this.renewKickoff()}>Renew Kickoff / Remind</Button>
                }
                {isGbCard(data) && hasTag(data.instrument, 'renewal')
                  && <ShipModal mapId={mapId} renewal />
                }
                {isGbCard(data) && !hasTag(data.instrument, 'user-inactive')
                  && (
                  <Modal
                    basic
                    open={modal === 'customer-cancel'}
                    trigger={(
                      <Popup
                        trigger={
                          <Button onClick={() => this.setModal('customer-cancel')}>Customer Cancellation</Button>
                        }
                        content="If the customer has decided to cancel/deactivate, this will disable their card. It will not remove the bank account."
                      />
)}
                  >
                    <Header icon="attention" content="Are you sure?" />
                    <Modal.Content>
                      <p>Are you sure you want to mark this card as canceled by the customer?</p>
                      <p>
The card will no longer work at the pump but can be reactivated by the
                      customer as long as they have a valid, verified bank account.
                      </p>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button basic color="grey" inverted onClick={() => this.setModal(null)}>
                        <Icon name="minus" />
                        {' '}
Leave Account As Is
                      </Button>
                      <Button basic color="red" onClick={() => this.setAuthProfile('user-inactive')}>
                        <Icon name="trash" />
                        {' '}
Deactivate Card
                      </Button>
                    </Modal.Actions>
                  </Modal>
                  )
                }
                {(isGbCard(data) || isCorPayCard(data)) && !isDeleted(data)
                  && (
                  <Modal
                    basic
                    open={modal === 'delete-gb'}
                    trigger={<Button negative onClick={() => this.setModal('delete-gb')}>Mark for Deletion</Button>}
                  >
                    <Header icon="attention" content="Are you sure?" />
                    <Modal.Content>
                      <p>
                        Are you sure you want to mark this
                        {isGbCard(data) ? ' Wex ' : ' PDI '}
                        card as deleted?
                        It will not change the ability of the card to transact
                        (change the profile for that), it will just take it out of the wallet.
                        This button is mostly useful for duplicate
                        cases where the account has the same card more than once.
                      </p>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button basic color="grey" inverted onClick={() => this.setModal(null)}>
                        <Icon name="minus" />
                        {' '}
Leave Card As Is
                      </Button>
                      <Button basic color="red" onClick={this.deleteInstrument}>
                        <Icon name="trash" />
                        {' '}
                        Mark
                        {isGbCard(data) ? ' Wex ' : ' PDI '}
                        Card as Deleted
                      </Button>
                    </Modal.Actions>
                  </Modal>
                  )
                }
                {isGbCard(data) && isDeleted(data) && hasLastDigits(data.instrument) && !isAccountDeleteRequested(data)
                  && (
                  <Modal
                    basic
                    open={modal === 'reactivate-gb'}
                    trigger={<Button negative onClick={() => this.setModal('reactivate-gb')}>Reactivate Card</Button>}
                  >
                    <Header icon="attention" content="Are you sure?" />
                    <Modal.Content>
                      <p>
Are you sure you want to reactivate this Wex card?
                      </p>
                      <ul>
                        <li>This card will be set to the initial state and will require a Driver ID reset.</li>
                        <li>The other card on this account will be deactivated.</li>
                      </ul>
                    </Modal.Content>
                    <Modal.Actions>
                      <Button basic color="grey" inverted onClick={() => this.setModal(null)}>
                        <Icon name="minus" />
                        {' '}
Leave Card As Is
                      </Button>
                      <Button basic color="red" onClick={this.reactivateWex}>
                        Reactivate this Card
                      </Button>
                    </Modal.Actions>
                  </Modal>
                  )
                }
                {isCorPayCard(data) && hasTag(data.instrument, 'initial') && !hasTag(data.instrument, 'returned')
                  && hasFulfillment(data) && <Button onClick={() => this.markAsReturned()}>Mark As Returned</Button>}
              </span>
            </Auth>
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <span>
                {isBank(data) && !hasTag(data.instrument, 'md-verified')
                  && !hasTag(data.instrument, 'verified')
                  && <Button onClick={this.verifyDwollaStatus}>Sync with Dwolla</Button>
                }
                {hasTag(data.instrument, 'nsf')
                  && <Button onClick={() => this.removeTag('nsf')}>Clear NSF</Button>
                }
              </span>
            </Auth>
            <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} hideUnauthorized>
              <span>
                {hasTag(data.instrument, 'balance-check')
                  && <Button onClick={() => this.removeAndRecordTags('balance-check')}>Clear Balance Check</Button>
                }
              </span>
            </Auth>
            <Auth forRoles={[Role.Superuser]} hideUnauthorized>
              <span>
                {isGbCard(data) && !isDeleted(data)
                  && (
                    <Button onClick={this.refreshToken}>Refresh Token</Button>
                  )}
              </span>
            </Auth>
            <Auth forRoles={[Role.Superuser, Role.PaymentDeleteBanks]} hideUnauthorized>
              {isBank(data) && !isDeleted(data) && (() => {
                const inst = data.instrument || {};
                const isAlreadyDefault = hasTag(inst, 'default-bank');
                const isVerified = hasTag(inst, 'verified');
                const isDisqualified = hasTag(inst, 'nsf') || hasTag(inst, 'inactive') || hasTag(inst, 'returned');
                if (isAlreadyDefault || !isVerified || isDisqualified) return null;
                return (
                  <Button
                    onClick={() => this.setAsDefault()}
                    disabled={loading}
                  >
                    Set as Default
                  </Button>
                );
              })()}
              {isBank(data) && !isDeleted(data)
                && (
                <Modal
                  basic
                  open={modal === 'delete'}
                  trigger={<Button negative onClick={() => this.setModal('delete')}>Mark for Deletion</Button>}
                >
                  <Header icon="attention" content="Are you sure?" />
                  <Modal.Content>
                    <p>Are you sure you want to delete this bank instrument?</p>
                    <p>
The customer will need to
                      <b>re-add the account,</b>
                      and their GasBuddy payment instruments will be disabled
                    </p>
                  </Modal.Content>
                  <Modal.Actions>
                    <Button basic color="grey" inverted onClick={() => this.setModal(null)}>
                      <Icon name="minus" />
                      {' '}
Leave Account As Is
                    </Button>
                    <Button basic color="red" onClick={this.deleteInstrument}>
                      <Icon name="trash" />
                      {' '}
Delete Bank Account
                    </Button>
                  </Modal.Actions>
                </Modal>
                )
              }
              {isBank(data) && isDeleted(data)
                && (
                <Button
                  onClick={this.undeleteInstrument}
                  loading={dwollaLoading}
                >
Undelete
                </Button>
                )
              }
            </Auth>
          </Container>
          <Divider />
          {(dwolla && dwolla.customer && dwolla.customer.status === 'deactivated')
            ? (
              <Message negative>
              Dwolla account is currently deactivated.
              &nbsp;
                <Auth forRoles={[Role.Superuser, Role.PaymentSupport]} unauthorized="Admin will have to reactivate">
                  <Button size="mini" onClick={this.reactivate}>Reactivate Customer</Button>
                </Auth>
              </Message>
            ) : null
          }
          {(dwolla && dwolla.customer && dwolla.customer.status === 'suspended')
            ? (
              <Message negative>
              Dwolla account is currently suspended. Admin will have to talk to Dwolla to unsuspend.
              </Message>
            ) : null
          }
          <Grid columns="equal">
            <Grid.Row>
              <Grid.Column>
                <Grid columns="equal">
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h3" block attached="top" style={{ flex: 0 }}>
                        Metadata
                      </Header>
                      <Segment attached>
                        {pre(data.instrument && data.instrument.extra_data)}
                      </Segment>
                      {data.profile
                          && (
                            <Segment attached>
                              <div>
                                <b>Card profile:</b>
                                &nbsp;
                                { data.profile }
                              </div>
                            </Segment>
                          )
                        }
                    </Grid.Column>
                    {isBank(data)
                      && (
                      <Grid.Column>
                        <Header as="h3" block attached="top" style={{ flex: 0 }}>
                          Bank Verification
                        </Header>
                        <Segment attached>
                          <div>
                            {data.verification && !!data.verification.length
                              && (
                              <List divided>
                                {data.verification.map(v => (
                                  <List.Item>
                                    {v.debit_external_transaction_id === null
                                      ? (
                                        <List.Content floated="right">
                                          <Button
                                            onClick={() => this.reverify(v.bank_verification_id)}
                                            floated="right"
                                            size="small"
                                          >
                                          Resubmit
                                          </Button>
                                        </List.Content>
                                      ) : null
                                    }
                                    {(v.debit_external_transaction_id && v.debit_status !== 'settled' && v.debit_status !== 'declined')
                                      ? (
                                        <List.Content floated="right">
                                          <Button
                                            onClick={() => this.investigate(v.bank_verification_id)}
                                            floated="right"
                                            size="small"
                                          >
                                          Get Details
                                          </Button>
                                        </List.Content>
                                      ) : null
                                    }
                                    <List.Icon
                                      name={bvIcon(v)}
                                      size="large"
                                      color={bvColor(v)}
                                      verticalAlign="middle"
                                    />
                                    <List.Content>
                                      <p>
                                        <b>{moment(v.created_at).format('YYYY-MM-DD')}</b>
                                        <br />
                                        <b>
$
                                          {new BN(v.amount).toFormat(2)}
                                        </b>
                                        <br />
                                        {v.credit_status
                                          && (
                                          <span>
                                            <b>Credit:</b>
                                            {' '}
                                            {v.credit_status}
                                            <br />
                                          </span>
                                          )
                                        }
                                        <b>Debit:</b>
                                        {' '}
                                        {v.debit_status}
                                        <br />
                                      </p>
                                    </List.Content>
                                  </List.Item>
                                ))}
                              </List>
                              )
                            }
                            {(!data.verification || data.verification.length === 0)
                              && <b>No Verifications</b>
                            }
                          </div>
                          {canRerunVerification(data)
                            && (
                            <Auth hideUnauthorized forRoles={[Role.Superuser, Role.PaymentSupport]}>
                              <Button onClick={this.rerunVerification}>
                                Re-Run Secondary Verification
                              </Button>
                            </Auth>
                            )
                          }
                        </Segment>

                        {data.failures && !!data.failures.length
                          && (
                          <div>
                            <Header as="h3" block attached="top" style={{ flex: 0 }}>
                              Transfer Failures
                            </Header>
                            <Segment attached>
                              <div>
                                <Table celled>
                                  <Table.Header>
                                    <Table.HeaderCell>Code</Table.HeaderCell>
                                    <Table.HeaderCell>Description</Table.HeaderCell>
                                    <Table.HeaderCell>Count</Table.HeaderCell>
                                  </Table.Header>
                                  <Table.Body>
                                    {data.failures.map(r => (
                                      <Table.Row>
                                        <Table.Cell>{r.code}</Table.Cell>
                                        <Table.Cell>{r.description}</Table.Cell>
                                        <Table.Cell>{r.count}</Table.Cell>
                                      </Table.Row>
                                    ))}
                                  </Table.Body>
                                </Table>
                              </div>
                            </Segment>
                          </div>
                          )
                        }
                      </Grid.Column>
                      )
                    }
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <NoteTable
                        notes={data && data.notes}
                        type="identity"
                        id={data && data.instrument && data.instrument.account_id}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    {data.fulfillments
                      && (
                      <Grid.Column>
                        <Header as="h3" block attached="top" style={{ flex: 0 }}>
                          Fulfillments
                        </Header>
                        <Segment attached>
                          <Table celled>
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell width={3}>Date</Table.HeaderCell>
                                <Table.HeaderCell>Address</Table.HeaderCell>
                                <Table.HeaderCell width={2} textAlign="center">Ready</Table.HeaderCell>
                                <Table.HeaderCell width={3}>Batch</Table.HeaderCell>
                                <Table.HeaderCell width={3}>Shipped</Table.HeaderCell>
                                <Table.HeaderCell>Personalization</Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {data.fulfillments && data.fulfillments.map(a => (
                                <Table.Row key={a.payment_instrument_fulfillment_id}>
                                  <Table.Cell>{moment(a.created_at).format('MM/DD/YY hh:mm')}</Table.Cell>
                                  <Table.Cell>
                                    {a.street1}
                                    <br />
                                    {a.street2 || ''}
                                    {a.street2 ? <br /> : null}
                                    {a.locality}
,
                                    {a.region}
                                    {' '}
                                    {a.postal_code}
                                  </Table.Cell>
                                  <Table.Cell>{a.ready_to_ship && <Icon name="checkmark" />}</Table.Cell>
                                  <Table.Cell textAlign="center">
                                    <div>
                                      {a.batch_identifier}
                                      <Auth
                                        forRoles={[Role.Superuser, Role.PaymentSupport]}
                                        hideUnauthorized
                                      >
                                        {!a.batch_identifier
                                          && (
                                          <Button
                                            onClick={() => this.cancelShip(a)}
                                          >
cancel
                                          </Button>
                                          )}
                                      </Auth>
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell>{a.ship_date ? moment(a.ship_date).format('MMM DD') : this.renderReleaseButton(a)}</Table.Cell>
                                  <Table.Cell>{pre(a.personalization_data)}</Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table>
                          <Auth
                            forRoles={[Role.Superuser, Role.PaymentReissueWex]}
                            hideUnauthorized
                          >
                            <ReissueModal instrument={data.instrument} />
                          </Auth>
                          <ShipModal mapId={mapId} renewal={isCardExpiringSoon(data.instrument)} instrument={data.instrument} />
                        </Segment>
                      </Grid.Column>
                      )
                    }
                    {isBank(data)
                      && (
                      <Grid.Column>
                        <Header as="h3" block attached="top" style={{ flex: 0 }}>
                          Lookalike Instruments
                        </Header>
                        <Table celled>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>Account Id</Table.HeaderCell>
                              <Table.HeaderCell>Instrument</Table.HeaderCell>
                              <Table.HeaderCell width={1}>Blocked</Table.HeaderCell>
                              <Table.HeaderCell>TPV</Table.HeaderCell>
                              <Table.HeaderCell>Owed</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {(data.lookalikes || []).map(i => (
                              <Table.Row>
                                <Table.Cell>
                                  <a href={`/identity/users/${i.account_id}`}>{i.account_id}</a>
                                </Table.Cell>
                                <Table.Cell>
                                  <a href={`/payments/instrument-map/${i.instrument_account_map_id}`}>{i.instrument_id}</a>
                                </Table.Cell>
                                <Table.Cell>
                                  {i.blocked_at && <Icon name="remove" />}
                                </Table.Cell>
                                <Table.Cell>
                                  $
                                  {new BN(i.tpv || 0).toFormat(2)}
                                </Table.Cell>
                                <Table.Cell>
                                  $
                                  {new BN(i.balance || 0).toFormat(2)}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                          <Auth
                            forRoles={[Role.Superuser, Role.PaymentSupport]}
                            hideUnauthorized
                          >
                            <Table.Footer>
                              <Table.Row>
                                <Table.Cell>
                                  {!data.blocked
                                    && (
                                    <Button onClick={e => this.blockAccount(e, true)}>
                                      Block Enrollments With This Bank Account
                                    </Button>
                                    )
                                  }
                                  {data.blocked
                                    && (
                                    <Button onClick={e => this.blockAccount(e, false)}>
                                      Remove Block
                                    </Button>
                                    )
                                  }
                                </Table.Cell>
                              </Table.Row>
                            </Table.Footer>
                          </Auth>
                        </Table>
                      </Grid.Column>
                      )
                    }
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              <Grid.Column width={6}>
                <Header as="h3" block attached="top" style={{ flex: 0 }}>
                  Audit Log
                </Header>
                <Segment attached>
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                        <Table.HeaderCell>Event</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.audit && data.audit.map(a => (
                        <Popup
                          key={a.audit_id}
                          wide="very"
                          on="click"
                          trigger={(
                            <Table.Row>
                              <Table.Cell>{moment(a.created).format('MMM DD, YYYY hh:mm:ss a ZZ')}</Table.Cell>
                              <Table.Cell>{a.eventDesc || a.event}</Table.Cell>
                            </Table.Row>
)}
                          header="Audit Detail"
                          content={(
                            <div style={{ minWidth: 500 }}>
                              <JSONTree
                                inverted
                                shouldExpandNode={() => true}
                                data={a}
                              />
                            </div>
)}
                        />
                      ))}
                    </Table.Body>
                  </Table>
                </Segment>

                {isGbCard(data) && !isDeleted(data) && (
                  <Segment>
                    <ChangeNameModal accountId={data?.instrument?.account_id} instrumentId={data?.instrument?.instrument_id} />
                    {hasTag(data.instrument, 'active') && <LimitConfirmationModal accountId={data?.instrument?.account_id} instrumentId={data?.instrument?.instrument_id} onChange={fetchFn} />}
                  </Segment>
                )}

                {isCorPayCard(data) && !isDeleted(data) && (
                  <Segment>
                    <ChangeNameModal accountId={data?.instrument?.account_id} instrumentId={data?.instrument?.instrument_id} />
                  </Segment>
                )}

                {dwolla
                  ? (
                    <div>
                      <Header as="h3" block attached="top" style={{ flex: 0 }}>
Dwolla
                      </Header>
                      <Segment attached>
                        <b>Source</b>
                        <JSONTree data={dwolla.source} hideRoot inverted />
                        <b>Customer</b>
                        <JSONTree data={dwolla.customer} hideRoot inverted />
                      </Segment>
                    </div>
                  ) : null}

              </Grid.Column>
            </Grid.Row>
          </Grid>

          {process.env.NODE_ENV !== 'production' && mapId
            && (
            <InstrumentTools
              instrument={data && data.instrument}
              fulfillments={data && data.fulfillments}
              mapId={mapId}
              onChange={fetchFn}
            />
            )
          }
        </div>
      </Segment>
    );
  }
}

InstrumentDetail.propTypes = {
  history: PT.shape({ push: PT.func }).isRequired,
  mapId: PT.string,
  data: PT.shape({
    instrument: PT.shape({
      account_id: PT.number,
      instrument_id: PT.number,
    }),
    created: PT.string,
    splits: PT.array,
    extra_data: PT.object,
  }),
  loading: PT.bool,
  fetch: PT.func.isRequired,
};

InstrumentDetail.defaultProps = {
  mapId: 0,
  data: {},
  loading: true,
};

export const Instrument = ({ match, history }) => (
  <Auth forRoles={[Role.Superuser, Role.PaymentView]}>
    <Nav>
      <WithData url={`/payments/instrument-map/${match.params.mapid}`}>
        <InstrumentDetail mapId={match.params.mapid} history={history} />
      </WithData>
    </Nav>
  </Auth>
);

Instrument.propTypes = {
  history: PT.shape({ push: PT.func }).isRequired,
  match: PT.shape({ params: PT.shape({ mapid: PT.string.isRequired }).isRequired }).isRequired,
};
